/* My Quotes — saved-quote list + rate-change alert + convert-to-book.
 * Ported verbatim from templates/ELCAPP.html. */
import { useState, useEffect, C, Ic } from './common.js';

const MyQuotes = ({ setScreen, setSQ, user }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rateAlert, setRateAlert] = useState(null);
  const [processing, setProcessing] = useState(null);

  const loadQuotes = () => {
    setLoading(true);
    fetch('/api/elcapp/quotes')
      .then(r => r.json())
      .then(d => { setQuotes(d.quotes || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadQuotes(); }, []);

  const handleConvert = async (q) => {
    setProcessing(q.quote_ref);
    setRateAlert(null);
    try {
      const rc = await fetch(`/api/elcapp/quotes/${q.quote_ref}/rate-check`).then(r => r.json());
      if (rc.error) { setProcessing(null); alert(rc.error); return; }
      if (rc.rate_changed) {
        setRateAlert({ ...rc, quoteData: q });
        setProcessing(null);
      } else {
        proceedToBook(q);
      }
    } catch {
      setProcessing(null);
      alert('Failed to check rate. Please try again.');
    }
  };

  const proceedToBook = (q) => {
    setRateAlert(null);
    setSQ({
      ...q,
      transit: q.transit_days ? `${q.transit_days} day${q.transit_days > 1 ? 's' : ''}` : '—',
    });
    setScreen('booking');
  };

  const statusStyle = (s) => ({
    saved:    { background: 'rgba(219,234,254,0.8)',  color: '#1d4ed8' },
    accepted: { background: 'rgba(220,252,231,0.75)', color: '#15803d' },
    expired:  { background: 'rgba(226,232,240,0.8)',  color: '#64748b' },
  }[s] || { background: 'rgba(226,232,240,0.8)', color: '#334155' });

  return <div style={{ maxWidth: 980, margin: '0 auto', padding: '36px 20px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 }}>
      <div>
        <h2 className="bc" style={{ fontSize: 30, fontWeight: 700 }}>MY SAVED QUOTES</h2>
        <p style={{ color: C.textM, fontSize: 13, marginTop: 4 }}>Quotes valid 7 days · Convert to booking before expiry</p>
      </div>
      <button className="btn bo" style={{ fontSize: 12 }} onClick={loadQuotes}>↻ Refresh</button>
    </div>

    {rateAlert && <div style={{ background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.3)', borderRadius: 8, padding: '18px 22px', marginBottom: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: C.gold, marginBottom: 8 }}>⚠ Rate Updated</div>
      <div style={{ fontSize: 13, color: C.textM, marginBottom: 14 }}>
        The rate for <strong>{rateAlert.quote_ref}</strong> has changed since you saved it.
      </div>
      <div style={{ display: 'flex', gap: 32, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 10, color: C.textD, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Original Price</div>
          <div className="mono bc" style={{ fontSize: 20, fontWeight: 700, textDecoration: 'line-through', color: C.textD }}>${rateAlert.old_price?.toFixed(2)}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', color: C.textD, fontSize: 18 }}>→</div>
        <div>
          <div style={{ fontSize: 10, color: C.textD, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Updated Price</div>
          <div className="mono bc" style={{ fontSize: 20, fontWeight: 700, color: C.gold }}>${rateAlert.new_price?.toFixed(2)}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn bg" style={{ fontSize: 12, padding: '8px 20px' }}
                disabled={!user.bookingEnabled}
                title={user.bookingEnabled ? undefined : 'Booking is not yet available'}
                onClick={() => proceedToBook(rateAlert.quoteData)}>Accept New Price & Book</button>
        <button className="btn bo" style={{ fontSize: 12, padding: '8px 16px' }} onClick={() => setRateAlert(null)}>Cancel</button>
      </div>
    </div>}

    {loading
      ? <div style={{ textAlign: 'center', padding: '80px 20px', color: C.textM }}>
          <div style={{ fontSize: 28, marginBottom: 12 }}>✈️</div>
          <div style={{ fontSize: 14 }}>Loading quotes…</div>
        </div>
      : !quotes.length
        ? <div style={{ textAlign: 'center', padding: '60px 20px', color: C.textM }}>
            <div style={{ fontSize: 32, marginBottom: 16 }}>📋</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No saved quotes yet</div>
            <div style={{ fontSize: 13, color: C.textD, marginBottom: 24 }}>Get a quote and save it to convert to a booking later.</div>
            <button className="btn bb" onClick={() => setScreen('quote')}>Get a Quote</button>
          </div>
        : <div style={{ display: 'grid', gap: 12 }}>
            {quotes.map(q => {
              const st = statusStyle(q.status);
              const isExpired = q.status === 'expired';
              const isSaved   = q.status === 'saved';
              return <div key={q.quote_ref} className="card" style={{ padding: '18px 22px', opacity: isExpired ? .6 : 1 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 110px 170px', gap: 16, alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 10, color: C.textD, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Quote Ref</div>
                    <div className="mono" style={{ fontSize: 13, fontWeight: 700, color: C.blue }}>{q.quote_ref}</div>
                    <div style={{ fontSize: 11, color: C.textD, marginTop: 3 }}>{q.origin} → {q.dest} · {q.commodity}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.textD, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Product</div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{q.product}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.textD, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Price / Weight</div>
                    <div className="mono bc" style={{ fontSize: 20, fontWeight: 700, color: C.green }}>${parseFloat(q.price).toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: C.textD, marginTop: 2 }}>{parseFloat(q.charge_weight).toFixed(1)} kg chargeable</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.textD, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>Status</div>
                    <span style={{ ...st, display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', padding: '3px 9px', borderRadius: 20 }}>{q.status}</span>
                    {!isExpired && q.valid_until && <div style={{ fontSize: 10, color: C.textD, marginTop: 5 }}>Until {new Date(q.valid_until).toLocaleDateString()}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {isSaved && <button className="btn bb" style={{ fontSize: 11, padding: '8px 16px', width: '100%' }}
                        disabled={processing === q.quote_ref || !user.bookingEnabled}
                        title={user.bookingEnabled ? undefined : 'Booking is not yet available — your quote stays saved.'}
                        onClick={() => handleConvert(q)}>
                      {processing === q.quote_ref ? 'Checking…' : (user.bookingEnabled ? 'Convert to Book' : 'Booking soon')}
                    </button>}
                    {q.status === 'accepted' && <div style={{ fontSize: 12, color: C.green, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5 }}><Ic.Check sz={13} col={C.green} /> Booked</div>}
                    {isExpired && <button className="btn bo" style={{ fontSize: 11, padding: '8px 16px', width: '100%' }} onClick={() => setScreen('quote')}>Re-quote</button>}
                  </div>
                </div>
              </div>;
            })}
          </div>
    }
  </div>;
};

export { MyQuotes };
